import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Note as NoteIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
  Build as BuildIcon,
} from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { appendVehicleTimelineNote } from '@/hooks/timeline'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import { timelineShipmentRepairQueryKey } from '@/hooks/logistics/useTimelineShipmentRepair'
import { uploadVehicleTimelineNoteAttachments } from '@/utils/vehicleTimelineNoteUpload'
import type { VehiclesV2Row } from '../views/VehiclesV2Grid'

const MANUAL_TRANSPORTER_DELIVERED_NOTE_TEXT = '__OTW_MANUAL_TRANSPORTER_DELIVERED__'

function isManualTransporterDeliveredNoteText(text: string): boolean {
  return text === MANUAL_TRANSPORTER_DELIVERED_NOTE_TEXT
}

function getStatusLabel(status?: string) {
  if (!status) return 'Draft'
  if (status === 'assigned') return 'Awaiting transport'
  if (status === 'awaiting_transport') return 'Awaiting transport'
  if (status === 'in_transit') return 'In transit'
  return status.replace(/_/g, ' ')
}

function statusLabelForChip(status?: string) {
  const label = getStatusLabel(status)
  if (!label) return ''
  return label.charAt(0).toUpperCase() + label.slice(1)
}

function statusChipColor(status: string): 'default' | 'info' | 'error' | 'warning' | 'success' | 'primary' {
  const isAwaitingTransport = status === 'assigned' || status === 'awaiting_transport'
  if (status === 'delivered') return 'success'
  if (status === 'in_transit') return 'primary'
  if (isAwaitingTransport) return 'info'
  if (status === 'repair') return 'error'
  return 'default'
}

export interface BulkCustomerVehiclesTimelineNoteDialogProps {
  open: boolean
  onClose: () => void
  rows: VehiclesV2Row[]
}

const BulkCustomerVehiclesTimelineNoteDialog: React.FC<BulkCustomerVehiclesTimelineNoteDialogProps> = ({
  open,
  onClose,
  rows,
}) => {
  const { userInfo } = useAuth()
  const { addRepairTruckNote, isAddingRepairTruckNote } = useLogistics()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedTruckIds, setSelectedTruckIds] = useState<Set<string>>(() => new Set())
  const [newNoteText, setNewNoteText] = useState('')
  const [newNoteKind, setNewNoteKind] = useState<'generic' | 'customer' | 'repair'>('generic')
  const [newNoteFiles, setNewNoteFiles] = useState<File[]>([])
  const addNoteFileInputRef = useRef<HTMLInputElement>(null)
  const [isSaving, setIsSaving] = useState(false)
  const prevOpenRef = useRef(false)

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setStep(1)
      setNewNoteText('')
      setNewNoteKind('generic')
      setNewNoteFiles([])
      setSelectedTruckIds(
        new Set(
          rows
            .filter((r) => r.status !== 'delivered' && (r.inventoryVehicleId || r.repairTruckId))
            .map((r) => r.id)
        )
      )
    }
    prevOpenRef.current = open
  }, [open, rows])

  const handleToggleTruck = (truckId: string, canSelect: boolean) => {
    if (!canSelect) return
    setSelectedTruckIds((prev) => {
      const next = new Set(prev)
      if (next.has(truckId)) next.delete(truckId)
      else next.add(truckId)
      return next
    })
  }

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedTruckIds.has(r.id)),
    [rows, selectedTruckIds]
  )
  const selectedWithVehicle = useMemo(
    () => selectedRows.filter((r) => r.inventoryVehicleId),
    [selectedRows]
  )
  const allSelectedHaveRepairTruck = useMemo(
    () => selectedRows.length > 0 && selectedRows.every((r) => Boolean(r.repairTruckId)),
    [selectedRows]
  )

  useEffect(() => {
    if (step === 2 && newNoteKind === 'repair' && !allSelectedHaveRepairTruck) {
      setNewNoteKind('generic')
      setNewNoteFiles([])
    }
  }, [step, newNoteKind, allSelectedHaveRepairTruck])

  const handleNextFromStep1 = () => {
    if (!selectedRows.some((r) => r.inventoryVehicleId || r.repairTruckId)) {
      enqueueSnackbar('Select at least one truck with inventory or an active repair record', { variant: 'warning' })
      return
    }
    setStep(2)
  }

  const handleConfirmNotes = async () => {
    const text = newNoteText.trim()
    if (newNoteKind === 'repair') {
      if (!text) {
        enqueueSnackbar('Enter a note', { variant: 'warning' })
        return
      }
    } else if (!text && newNoteFiles.length === 0) {
      enqueueSnackbar('Enter a note or add at least one attachment', { variant: 'warning' })
      return
    }
    if (text && isManualTransporterDeliveredNoteText(text)) {
      enqueueSnackbar('Use Mark delivered on each vehicle timeline to record delivery.', { variant: 'warning' })
      return
    }

    if (newNoteKind === 'repair') {
      const targets = selectedRows.filter((r) => r.repairTruckId)
      if (targets.length === 0) {
        enqueueSnackbar('No trucks with an active repair record in the selection', { variant: 'warning' })
        return
      }
      if (targets.length !== selectedRows.length) {
        enqueueSnackbar('Repair notes require every selected truck to have a repair record', { variant: 'warning' })
        return
      }
    } else if (selectedWithVehicle.length === 0) {
      enqueueSnackbar('No eligible trucks selected', { variant: 'warning' })
      return
    }

    setIsSaving(true)
    let successCount = 0
    try {
      if (newNoteKind === 'repair') {
        for (const row of selectedRows) {
          if (!row.repairTruckId) continue
          await addRepairTruckNote({
            repairTruckId: row.repairTruckId,
            note: text,
            suppressSuccessToast: true,
          })
          successCount += 1
          if (row.inventoryVehicleId) {
            await queryClient.invalidateQueries({
              queryKey: timelineShipmentRepairQueryKey(row.inventoryVehicleId),
            })
          }
        }
        enqueueSnackbar(
          successCount === 1 ? 'Repair note added to 1 truck' : `Repair note added to ${successCount} trucks`,
          { variant: 'success' }
        )
        onClose()
      } else {
        for (const row of selectedWithVehicle) {
          const vehicleId = row.inventoryVehicleId as string
          let attachments: Awaited<ReturnType<typeof uploadVehicleTimelineNoteAttachments>> | undefined
          if (newNoteFiles.length > 0) {
            attachments = await uploadVehicleTimelineNoteAttachments(vehicleId, newNoteFiles)
          }
          await appendVehicleTimelineNote(vehicleId, {
            text: text || '',
            ...(userInfo?.id && { created_by_user_id: userInfo.id }),
            category: newNoteKind === 'customer' ? 'customer' : 'generic',
            ...(attachments && attachments.length > 0 && { attachments }),
          })
          successCount += 1
          await queryClient.invalidateQueries({ queryKey: ['vehicle_timeline', vehicleId] })
        }
        enqueueSnackbar(
          successCount === 1 ? 'Note added to 1 truck' : `Note added to ${successCount} trucks`,
          { variant: 'success' }
        )
        onClose()
      }
    } catch (error) {
      console.error('Bulk timeline note error:', error)
      enqueueSnackbar(
        successCount > 0
          ? `Added to ${successCount} truck(s), then failed. Fix issues and retry for the rest.`
          : 'Failed to add notes',
        { variant: 'error' }
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleDialogClose = () => {
    if (isSaving || isAddingRepairTruckNote) return
    setNewNoteFiles([])
    onClose()
  }

  const skippedNoVehicle = selectedRows.length - selectedWithVehicle.length
  const isBusy = isSaving || isAddingRepairTruckNote

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth={step === 1 ? 'md' : 'sm'} fullWidth>
      <DialogTitle>{step === 1 ? 'Select trucks' : 'Write note'}</DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <>
            <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {rows.map((row) => {
                const canSelect = Boolean(row.inventoryVehicleId || row.repairTruckId)
                const checked = selectedTruckIds.has(row.id)
                return (
                  <ListItem key={row.id} disablePadding>
                    <ListItemButton
                      dense
                      disabled={!canSelect}
                      onClick={() => handleToggleTruck(row.id, canSelect)}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Checkbox
                          edge="start"
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          disabled={!canSelect}
                          inputProps={{ 'aria-labelledby': `bulk-truck-${row.id}` }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        id={`bulk-truck-${row.id}`}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2">{row.vehicle}</Typography>
                            <Chip
                              size="small"
                              label={statusLabelForChip(row.status)}
                              color={statusChipColor(row.status)}
                            />
                            {!canSelect ? (
                              <Chip size="small" variant="outlined" label="No inventory or repair record" />
                            ) : null}
                          </Box>
                        }
                        secondary={`Stock ${row.stock} · VIN ${row.vin}`}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {selectedRows.length} truck{selectedRows.length === 1 ? '' : 's'} selected
              {skippedNoVehicle > 0
                ? ` (${skippedNoVehicle} without inventory — generic or customer notes apply only to trucks with inventory)`
                : ''}
            </Typography>
          </>
        ) : (
          <>
            <ToggleButtonGroup
              value={newNoteKind}
              exclusive
              fullWidth
              size="small"
              onChange={(_, value: 'generic' | 'customer' | 'repair' | null) => {
                if (!value) return
                if (value === 'repair' && !allSelectedHaveRepairTruck) return
                if (value === 'repair') setNewNoteFiles([])
                setNewNoteKind(value)
              }}
              sx={{
                mb: 2,
                '& > *': { flex: '1 1 0', minWidth: 0 },
                '& > span > .MuiToggleButton-root': { width: '100%' },
              }}
            >
              <ToggleButton value="generic" sx={{ textTransform: 'none' }}>
                <NoteIcon fontSize="small" sx={{ mr: 0.75 }} />
                Generic
              </ToggleButton>
              <ToggleButton value="customer" sx={{ textTransform: 'none' }}>
                <PersonIcon fontSize="small" sx={{ mr: 0.75 }} />
                Customer
              </ToggleButton>
              <Tooltip
                title={
                  allSelectedHaveRepairTruck
                    ? 'Adds a repair shop note (right side of each truck timeline)'
                    : 'Every selected truck must have an active repair record at its current location'
                }
                placement="top"
              >
                <span style={{ display: 'flex' }}>
                  <ToggleButton
                    value="repair"
                    disabled={!allSelectedHaveRepairTruck}
                    sx={{ textTransform: 'none' }}
                  >
                    <BuildIcon fontSize="small" sx={{ mr: 0.75 }} />
                    Repair
                  </ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {newNoteKind === 'customer'
                ? 'Customer notes appear on the timeline with distinct styling. You can attach files.'
                : newNoteKind === 'repair'
                  ? 'Repair notes appear on the right side of the timeline alongside repair shop activity.'
                  : 'Generic notes appear on the timeline as standard notes. You can attach files.'}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              placeholder={
                newNoteKind === 'customer'
                  ? 'Enter customer note...'
                  : newNoteKind === 'repair'
                    ? 'Enter repair note...'
                    : 'Enter note...'
              }
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              sx={{ mt: 1 }}
            />
            {newNoteKind !== 'repair' ? (
              <>
                <input
                  ref={addNoteFileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    const list = e.target.files
                    if (!list?.length) return
                    setNewNoteFiles((prev) => [...prev, ...Array.from(list)].slice(0, 20))
                    e.target.value = ''
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    onClick={() => addNoteFileInputRef.current?.click()}
                    disabled={isBusy || newNoteFiles.length >= 20}
                  >
                    Add attachments
                  </Button>
                </Box>
                {newNoteFiles.length > 0 ? (
                  <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {newNoteFiles.map((file, fileIndex) => (
                      <Chip
                        key={`${file.name}-${file.size}-${fileIndex}`}
                        label={file.name}
                        size="small"
                        onDelete={() =>
                          setNewNoteFiles((prev) => prev.filter((_, chipIndex) => chipIndex !== fileIndex))
                        }
                      />
                    ))}
                  </Box>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {step === 1 ? (
          <>
            <Button onClick={handleDialogClose} variant="outlined" disabled={isBusy}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleNextFromStep1} variant="contained" disabled={isBusy}>
              Next
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(1)} variant="outlined" disabled={isBusy}>
              Back
            </Button>
            <Button onClick={handleDialogClose} variant="outlined" disabled={isBusy}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleConfirmNotes}
              variant="contained"
              disabled={
                isBusy ||
                (newNoteKind === 'repair'
                  ? !newNoteText.trim()
                  : !newNoteText.trim() && newNoteFiles.length === 0)
              }
            >
              {isBusy ? 'Saving...' : 'Confirm'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default BulkCustomerVehiclesTimelineNoteDialog
